// CityMassageTherapyPage.jsx - City Listing Page with Clinics
"use client";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Phone, Star, ArrowRight } from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import ChatBot from "./ChatBot";
import { API_ENDPOINTS, DEFAULT_FETCH_CONFIG } from "../config/api.config";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

export default function CityMassageTherapyPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCity = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          API_ENDPOINTS.seo.getCity(citySlug),
          DEFAULT_FETCH_CONFIG,
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError("City not found");
          } else {
            setError("Failed to load city");
          }
          return;
        }

        const data = await response.json();
        setCity(data);
      } catch (err) {
        console.error("Error fetching city:", err);
        setError("Failed to load city");
      } finally {
        setLoading(false);
      }
    };

    if (citySlug) {
      fetchCity();
    }
  }, [citySlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#98C454] border-t-transparent mx-auto"></div>
          <p className="mt-4 text-[#252F4F] font-medium">
            Loading city page...
          </p>
        </div>
      </div>
    );
  }

  if (error || !city) {
    return (
      <div className="min-h-screen bg-white">
        <NavbarDemo />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4 text-[#252F4F]">
            City Not Found
          </h1>
          <p className="text-[#252F4F]/70 mb-8">
            {error || "The city page you're looking for doesn't exist."}
          </p>
          <Button
            className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] font-semibold"
            onClick={() => navigate("/")}>
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const title =
    city.metaTitle ||
    `Massage Therapy in ${city.name}${
      city.province ? `, ${city.province}` : ""
    } | BookaTherapy`;

  const description =
    city.metaDescription ||
    `Find and book massage therapy appointments in ${city.name}${
      city.province ? `, ${city.province}` : ""
    }. ${
      city.clinicsCount || 0
    }+ trusted clinics. Easy online booking with BookaTherapy.`;

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical={`https://bookatherapy.com/${city.slug}/massage-therapy`}
        ogType="website"
      />

      <div className="min-h-screen bg-white">
        <NavbarDemo />
        <ChatBot />

        {/* Hero Section - Brand Navy & Lime */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#252F4F] via-[#1e2640] to-[#252F4F] pt-20 pb-16">
          {/* Subtle animated background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-96 h-96 bg-[#98C454]/30 rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
            <div className="absolute top-40 right-10 w-96 h-96 bg-[#98C454]/20 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
              <span className="text-[#98C454]">Massage Therapy</span>
              <br />
              <span>
                in {city.name}
                {city.province && `, ${city.province}`}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl">
              {city.clinicsCount || 0}+ trusted clinics. Book your appointment
              online.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* City Description */}
          {city.metaDescription && (
            <div className="mb-12">
              <p className="text-lg text-[#252F4F] leading-relaxed">
                {city.metaDescription}
              </p>
            </div>
          )}

          {/* Clinics List */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-[#252F4F]">
              Clinics in {city.name} ({city.clinics?.length || 0})
            </h2>

            {!city.clinics || city.clinics.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-600 text-lg mb-4">
                  No clinics found in {city.name} yet.
                </p>
                <Button onClick={() => navigate("/book")}>
                  Search for Therapists
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {city.clinics.map((clinic) => (
                  <Card
                    key={clinic.id || clinic._id}
                    className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-gray-200 hover:border-[#98C454]/30 group"
                    onClick={() => navigate(`/clinics/${clinic.slug}`)}>
                    {clinic.heroImage && (
                      <img
                        src={clinic.heroImage}
                        alt={clinic.clinicName}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-semibold mb-2 text-[#252F4F] group-hover:text-[#98C454] transition-colors">
                        {clinic.clinicName}
                      </h3>
                      {clinic.seoDescription && (
                        <p className="text-gray-600 mb-4 line-clamp-2 text-sm">
                          {clinic.seoDescription}
                        </p>
                      )}
                      {clinic.address && (
                        <div className="flex items-start mb-2">
                          <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-1 flex-shrink-0" />
                          <p className="text-sm text-gray-500">
                            {clinic.address}
                          </p>
                        </div>
                      )}
                      {clinic.phone && (
                        <div className="flex items-start mb-2">
                          <Phone className="h-4 w-4 text-gray-400 mr-2 mt-1 flex-shrink-0" />
                          <p className="text-sm text-gray-500">
                            {clinic.phone}
                          </p>
                        </div>
                      )}
                      {clinic.services && clinic.services.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-4">
                          {clinic.services.slice(0, 3).map((service, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-1 bg-[#98C454]/10 text-[#252F4F] border border-[#98C454]/30 rounded font-medium">
                              {service}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-4 text-[#252F4F] group-hover:text-[#98C454] font-semibold text-sm flex items-center transition-colors">
                        View Details{" "}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Call to Action */}
          <Card className="p-8 md:p-12 bg-gradient-to-br from-[#98C454]/10 to-[#98C454]/5 text-center border-[#98C454]/20 shadow-lg">
            <h3 className="text-2xl font-bold mb-4 text-[#252F4F]">
              Can't find what you're looking for?
            </h3>
            <p className="text-[#252F4F]/80 mb-6 max-w-2xl mx-auto">
              Search for therapists offering home, mobile, or clinic services in{" "}
              {city.name}.
            </p>
            <Button
              size="lg"
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] font-semibold shadow-lg transition-all duration-300"
              onClick={() => navigate("/book")}>
              Search Therapists
            </Button>
          </Card>
        </div>
      </div>
    </>
  );
}
